import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { MeasurementUnitInput } from '../../model/types';

interface MeasurementUnitsEditorProps {
  units: MeasurementUnitInput[];
  /** Unit codes already persisted server-side — their `code` becomes immutable and they cannot be removed. */
  existingCodes: Set<string>;
  standardUnitCode: string;
  onChange: (units: MeasurementUnitInput[]) => void;
  disabled?: boolean;
}

/**
 * Client-side units list editor for a measurement family (user-story-catalog-013 AC1/AC6). Units
 * are upserted **by code** in the family's own save request (no per-unit endpoint) — an already-
 * persisted unit's `code` is immutable (renaming it would create a new unit, orphaning the old
 * one) and cannot be removed here (the API upserts/adds by code; it never deletes on omission).
 * Only rows added in the current, unsaved session can be edited freely or removed again.
 */
export function MeasurementUnitsEditor({
  units,
  existingCodes,
  standardUnitCode,
  onChange,
  disabled,
}: MeasurementUnitsEditorProps) {
  const { t } = useTranslation('catalog');

  const updateRow = (index: number, patch: Partial<MeasurementUnitInput>) => {
    onChange(units.map((unit, i) => (i === index ? { ...unit, ...patch } : unit)));
  };

  const addRow = () => {
    onChange([...units, { code: '', conversionFactor: 1, sortOrder: units.length }]);
  };

  const removeRow = (index: number) => {
    onChange(units.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('measurementUnitsEditor.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('measurementUnitsEditor.removeHint')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('measurementUnitsEditor.table.code')}</TableCell>
            <TableCell align="right">{t('measurementUnitsEditor.table.conversionFactor')}</TableCell>
            <TableCell align="right">{t('measurementUnitsEditor.table.sortOrder')}</TableCell>
            {!disabled ? <TableCell align="right" /> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {units.map((unit, index) => {
            const isExisting = existingCodes.has(unit.code);
            const isStandard = unit.code !== '' && unit.code === standardUnitCode;
            return (
              <TableRow key={isExisting ? unit.code : `new-${index}`} hover>
                <TableCell>
                  <TextField
                    size="small"
                    value={unit.code}
                    onChange={(e) => updateRow(index, { code: e.target.value })}
                    disabled={disabled || isExisting}
                    slotProps={{
                      htmlInput: {
                        style: { fontFamily: 'JetBrains Mono, monospace' },
                        'aria-label': t('measurementUnitsEditor.table.code'),
                      },
                    }}
                  />
                  {isStandard ? (
                    <Tooltip title={t('measurementUnitsEditor.standardUnitTooltip')}>
                      <StarRoundedIcon fontSize="small" color="warning" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={unit.conversionFactor}
                    onChange={(e) => updateRow(index, { conversionFactor: Number(e.target.value) })}
                    disabled={disabled}
                    sx={{ width: 120 }}
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        step: 'any',
                        'aria-label': t('measurementUnitsEditor.table.conversionFactor'),
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={unit.sortOrder ?? 0}
                    onChange={(e) => updateRow(index, { sortOrder: Number(e.target.value) })}
                    disabled={disabled}
                    sx={{ width: 88 }}
                    slotProps={{
                      htmlInput: { step: 1, 'aria-label': t('measurementUnitsEditor.table.sortOrder') },
                    }}
                  />
                </TableCell>
                {!disabled ? (
                  <TableCell align="right">
                    {!isExisting ? (
                      <Tooltip title={t('common:action.remove')}>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={t('measurementUnitsEditor.removeAria', { code: unit.code || index + 1 })}
                          onClick={() => removeRow(index)}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {!disabled ? (
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={addRow} sx={{ mt: 1 }}>
          {t('measurementUnitsEditor.addUnit')}
        </Button>
      ) : null}
    </Box>
  );
}
